/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/PartnersImage/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./PartnersImage.module.css";

import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/icons/PartnersImage/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/PartnersImage/DeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/PartnersImage/AddIcon.svg";
import { ReactComponent as TitleIcon } from "../../assets/icons/PartnersImage/TitleIcon.svg";

import { useState, useEffect, useRef } from "react";
import AssetImage from "../AssetImage";
import { useNavigate } from "react-router";
import PartnersImageFormModal from "../../modals/PartnersImageFormModal";
import ConfirmationModal from "../../modals/ConfirmationModal";
import {
  getPartnersImage,
  deletePartnersImage,
} from "../../logic/partnersImage";
import { useTranslation } from "react-i18next";

const PARTNERS_IMAGE_HEADER_KEY = "components.partnersImage.header";

const PartnersImageHeader = ({ t, children = null }) => (
  <div className={styles.partnersImageHeader}>
    <div className={styles.partnersImageHeaderTitle}>
      <TitleIcon className={styles.partnersImageTitleIcon} />
      <h2 className={styles.partnersImageTitle}>{t(PARTNERS_IMAGE_HEADER_KEY)}</h2>
    </div>
    {children}
  </div>
);

const PartnersImage = ({ permissions, currentUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPartnersImage, setSelectedPartnersImage] = useState(null);
  const [partnersImage, setPartnersImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Dialog refs
  const confirmationDialogRef = useRef(null);

  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;
  const isNotComputer = isTablet || isMediumMobile || isSmallMobile;

  useEffect(() => {
    fetchPartner();
  }, []);

  const fetchPartner = async () => {
    try {
      setLoading(true);
      setError(null);
      const partnersData = await getPartnersImage(navigate);
      setPartnersImage(partnersData);
    } catch (error) {
      logger.error("Error fetching partner:", { err: sanitizeError(error) });
      setError(t("components.featuredSections.errorLoadingPartner"));
      setPartnersImage(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePartnersImage = () => {
    setEditMode(false);
    setSelectedPartnersImage(null);
    setModalOpen(true);
  };

  const handleEditPartnersImage = () => {
    setEditMode(true);
    setSelectedPartnersImage(partnersImage);
    setModalOpen(true);
  };

  const handleDeletePartnersImage = () => {
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      const result = await deletePartnersImage(partnersImage._id, navigate);
      if (result) {
        await fetchPartner();
        logger.info("Partner deleted successfully");
      } else {
        logger.error("Failed to delete partner");
      }
    } catch (error) {
      logger.error("Error deleting partner:", { err: sanitizeError(error) });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setIsDeleting(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditMode(false);
    setSelectedPartnersImage(null);
  };

  const handlePartnersImageCreated = async (partnerData) => {
    await fetchPartner();
    logger.info("Partner created successfully");
  };

  const handlePartnersImageUpdated = async (partnerData) => {
    await fetchPartner();
    logger.info("Partner updated successfully");
  };

  if (loading) {
    return (
      <div className={styles.partnersImageSection}>
        <PartnersImageHeader t={t} />
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={loading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.partnersImageSection}>
        <PartnersImageHeader t={t} />
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.partnersImageSection}>
      <PartnersImageHeader t={t}>
        {permissions?.find((permission) =>
          permission.hasOwnProperty("editHomepage"),
        ).editHomepage &&
          currentUser &&
          currentUser?.tipoDeUsuario !== "4" &&
          currentUser?.tipoDeUsuario !== "12" &&
          !isNotComputer && (
            <button
              className={styles.createPartnersImageButton}
              onClick={handleCreatePartnersImage}
              disabled={partnersImage !== null}
            >
              <AddIcon />
              {t("components.partnersImage.createPartnersImageButton")}
            </button>
          )}
      </PartnersImageHeader>

      {!partnersImage ? (
        <div className={styles.noPartnersMessage}>
          {t("components.partnersImage.noPartnersMessage")}
        </div>
      ) : (
        <div className={styles.partnersImageContainer}>
          <AssetImage
            src={partnersImage.imageUrl}
            alt={t("common.partnerImageAlt", { id: partnersImage._id })}
            className={styles.partnersImage}
          />
          {permissions?.find((permission) =>
            permission.hasOwnProperty("editHomepage"),
          ).editHomepage &&
            currentUser &&
            currentUser?.tipoDeUsuario !== "4" &&
            currentUser?.tipoDeUsuario !== "12" &&
            !isNotComputer && (
              <div className={styles.partnersImageActions}>
                <button
                  className={styles.editPartnersImageButton}
                  onClick={handleEditPartnersImage}
                  title={t("components.partnersImage.editPartnersImageButton")}
                >
                  <EditIcon />
                </button>
                <button
                  className={styles.deletePartnersImageButton}
                  onClick={handleDeletePartnersImage}
                  title={t(
                    "components.partnersImage.deletePartnersImageButton",
                  )}
                >
                  <DeleteIcon />
                </button>
              </div>
            )}
        </div>
      )}

      <PartnersImageFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        currentPartnersImage={selectedPartnersImage}
        editMode={editMode}
        onPartnersImageCreated={handlePartnersImageCreated}
        onPartnersImageUpdated={handlePartnersImageUpdated}
      />

      {deleteConfirmationOpen && (
        <ConfirmationModal
          dialogRef={confirmationDialogRef}
          open={deleteConfirmationOpen}
          onClose={handleCloseDeleteConfirmation}
          onConfirm={handleConfirmDelete}
          title={t("components.confirmationModal.title")}
          message={t("components.confirmationModal.message")}
          confirmButtonText={t("components.confirmationModal.confirm")}
          cancelButtonText={t("components.confirmationModal.cancel")}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default PartnersImage;
